/**
 * English: AI tools
 *
 * What this file covers: The AI tools page: writing with skills, answers from the business's documents, image to text, saved skills and the history of AI calls with what each cost.
 *
 * Tone: Friendly and plain, for business owners trying AI for the first time. Say what each tool does and what it costs in a few words; never promise more than the AI can do.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const ai: Catalog = {
  'ai.title': 'AI tools',
  'ai.description': 'Write, summarise, translate, answer from your own documents and read pictures, with the AI model you choose. Every use is charged to your credits.',
  'ai.banner': 'The same tools work from your own systems with an API key that has the AI permission.',
  'ai.banner.keys': 'API keys',
  'ai.tabs.write': 'Write',
  'ai.tabs.ask': 'Ask your documents',
  'ai.tabs.vision': 'Image to text',
  'ai.tabs.skills': 'My skills',
  'ai.tabs.history': 'History',
  'ai.chargeNote': 'The most it can cost is set aside from your credits first, and you are charged only what the AI really used.',
  'ai.model.label': 'Model',
  'ai.model.none': 'No AI model is switched on at the moment.',
  'ai.model.unpriced': 'No price yet.',
  'ai.model.priceText': '{{read}} per 10 000 tokens it reads, {{written}} per 10 000 it writes (a token is about four letters).',
  'ai.model.priceUnit': '{{price}} per {{unit}}.',
  'ai.model.outsideSa': 'Runs outside South Africa',
  'ai.result.copy': 'Copy',
  'ai.result.copied': 'Copied.',
  'ai.result.stub': 'AI is not set up on this platform yet, so this is a stand-in answer and nothing real was asked.',
  'ai.result.cost': '{{model}} · {{cost}} · read {{read}} tokens, wrote {{written}}',
  'ai.error.credits.title': 'Not enough credit',
  'ai.error.credits.body': 'This could cost up to {{needed}} and you have {{available}} free to spend. Top up, or ask for something shorter.',
  'ai.error.credits.topUp': 'Top up',
  'ai.error.AI_MODEL_UNAVAILABLE': 'No AI model is switched on at the moment. Please try again later.',
  'ai.error.AI_MODEL_NOT_PRICED': 'That model has no price yet, so it cannot be used. Choose another.',
  'ai.error.AI_PROVIDER_ERROR': 'The AI could not answer just now. Nothing was charged. Please try again.',
  'ai.error.BILLING_UNAVAILABLE': 'Your credit could not be checked just now, so nothing was run. Please try again in a moment.',
  'ai.write.title': 'Write with a skill',
  'ai.write.skill': 'Skill',
  'ai.write.own': '{{name}} (yours)',
  'ai.write.run': 'Write it',
  'ai.skill.summarise.name': 'Summarise',
  'ai.skill.summarise.description': 'Shortens a text to what matters.',
  'ai.skill.write.name': 'Write',
  'ai.skill.write.description': 'Writes an article, post, email, newsletter, message or story about a topic.',
  'ai.skill.rewrite.name': 'Rewrite',
  'ai.skill.rewrite.description': 'Says the same thing in another tone, or more clearly.',
  'ai.skill.translate.name': 'Translate',
  'ai.skill.translate.description': 'Translates a text into another language.',
  'ai.skill.reply.name': 'Draft a reply',
  'ai.skill.reply.description': 'Drafts a reply to a customer\'s message.',
  'ai.skill.extract.name': 'Pull out details',
  'ai.skill.extract.description': 'Finds named details in a text, such as a name, an email or an order number.',
  'ai.skill.classify.name': 'Sort into categories',
  'ai.skill.classify.description': 'Says which of your categories a text belongs to.',
  'ai.input.default': 'Let the AI decide',
  'ai.input.text.label': 'Text',
  'ai.input.text.hint': 'The text to work on.',
  'ai.input.topic.label': 'Topic',
  'ai.input.topic.hint': 'What to write about, e.g. our school festival on Saturday.',
  'ai.input.kind.label': 'What to write',
  'ai.input.kind.hint': 'The kind of writing.',
  'ai.input.tone.label': 'Tone',
  'ai.input.tone.hint': 'How it should sound, e.g. friendly, formal, fun or persuasive.',
  'ai.input.length.label': 'Length',
  'ai.input.length.hint': 'How long it should be.',
  'ai.input.audience.label': 'Audience',
  'ai.input.audience.hint': 'Who it is for, e.g. parents, customers or staff.',
  'ai.input.details.label': 'Details',
  'ai.input.details.hint': 'Facts to include: dates, names, prices and places. The AI never makes them up.',
  'ai.input.language.label': 'Language',
  'ai.input.language.hint': 'The language to write in, e.g. English, Afrikaans or isiZulu.',
  'ai.input.format.label': 'Layout',
  'ai.input.format.hint': 'Paragraphs or bullet points.',
  'ai.input.message.label': 'The customer\'s message',
  'ai.input.message.hint': 'What the customer wrote.',
  'ai.input.fields.label': 'Details to find',
  'ai.input.fields.hint': 'Separated by commas, e.g. name, email, order number, date.',
  'ai.input.labels.label': 'Categories',
  'ai.input.labels.hint': 'Separated by commas, e.g. billing, complaint, sales, other.',
  'ai.choice.short': 'Short',
  'ai.choice.medium': 'Medium',
  'ai.choice.long': 'Long',
  'ai.choice.paragraph': 'Paragraphs',
  'ai.choice.bullets': 'Bullet points',
  'ai.choice.article': 'Article',
  'ai.choice.social_post': 'Social media post',
  'ai.choice.email': 'Email',
  'ai.choice.newsletter': 'Newsletter',
  'ai.choice.whatsapp_message': 'WhatsApp message',
  'ai.choice.story': 'Story',
  'ai.choice.product_description': 'Product description',
  'ai.ask.title': 'Ask your documents',
  'ai.ask.body': 'The AI answers only from your knowledge base and shows where the answer came from. Add documents on the page',
  'ai.ask.question': 'Question',
  'ai.ask.questionHint': 'Ask it the way a customer or a colleague would.',
  'ai.ask.run': 'Ask',
  'ai.ask.unsure': 'Your documents do not say. Add the answer to your knowledge base, or ask in another way.',
  'ai.ask.sources': 'Where it looked',
  'ai.ask.source': '{{name}}, page {{page}}',
  'ai.vision.title': 'Image to text',
  'ai.vision.picture': 'Picture',
  'ai.vision.pictureHint': 'A PNG, JPEG, GIF or WebP picture under 3,75 MB.',
  'ai.vision.wrongType': 'Choose a PNG, JPEG, GIF or WebP picture.',
  'ai.vision.tooBig': 'That picture is too big. Choose one under 3,75 MB.',
  'ai.vision.task': 'What to do',
  'ai.vision.task.describe': 'Describe the picture',
  'ai.vision.task.text': 'Read every word on it',
  'ai.vision.task.question': 'Answer a question about it',
  'ai.vision.question': 'Question',
  'ai.vision.questionHint': 'e.g. What is the total on this invoice?',
  'ai.vision.run': 'Read it',
  'ai.skills.title': 'My skills',
  'ai.skills.body': 'Save instructions, background and a tone once, then run them from Write or from your own systems.',
  'ai.skills.new': 'New skill',
  'ai.skills.empty': 'You have no skills of your own yet',
  'ai.skills.emptyHint': 'For example: a school newsletter in a fun tone, with your school\'s name and values as background.',
  'ai.skills.saved': '{{name}} is saved.',
  'ai.skills.deleted': '{{name}} is deleted.',
  'ai.skills.deleteTitle': 'Delete {{name}}?',
  'ai.skills.deleteBody': 'Systems that run this skill by its code will be told it no longer exists.',
  'ai.skills.name': 'Name',
  'ai.skills.nameHint': 'What you call it, e.g. School newsletter.',
  'ai.skills.code': 'Code',
  'ai.skills.codeHint': 'How your systems name it: lower-case letters, digits and hyphens.',
  'ai.skills.description': 'Description',
  'ai.skills.descriptionHint': 'One line that says what it is for.',
  'ai.skills.instructions': 'Instructions',
  'ai.skills.instructionsHint': 'What the AI should do, as you would tell a person, e.g. Write a newsletter article for parents about the topic given.',
  'ai.skills.context': 'Background',
  'ai.skills.contextHint': 'What the AI should always know: who you are, your audience, facts and phrases to use.',
  'ai.skills.tone': 'Tone',
  'ai.skills.toneHint': 'e.g. fun, warm, formal.',
  'ai.skills.creativity': 'Creativity',
  'ai.skills.creativityHint': 'Precise sticks closely to what it is given; creative writes more freely. Claude models choose this for themselves, so it applies to the Nova models.',
  'ai.skills.creativity.precise': 'Precise',
  'ai.skills.creativity.balanced': 'Balanced',
  'ai.skills.creativity.creative': 'Creative',
  'ai.skills.maxTokens': 'Longest answer (tokens)',
  'ai.skills.maxTokensHint': 'About four letters to a token. A longer limit sets more credit aside while it runs.',
  'ai.skills.kb': 'Use my knowledge base',
  'ai.skills.kbHint': 'Also give the AI the passages from your documents that match what it is asked.',
  'ai.history.title': 'History',
  'ai.history.body': 'Every AI call from this page and from your API keys, with what it cost.',
  'ai.history.empty': 'No AI calls yet.',
  'ai.history.when': 'When',
  'ai.history.tool': 'Tool',
  'ai.history.skill': 'Skill: {{skill}}',
  'ai.history.by': 'By',
  'ai.history.byKey': 'API key {{name}}',
  'ai.history.byConsole': 'This page',
  'ai.history.cost': 'Cost',
  'ai.history.preview': 'Answer',
  'ai.tool.text': 'Text',
  'ai.tool.vision': 'Image to text',
  'ai.tool.answer': 'Ask your documents',
  'ai.tool.skill': 'Skill',
  'ai.tool.speech': 'Text to speech',
  'ai.tool.transcribe': 'Speech to text',
  'ai.tool.image': 'Picture',
  'ai.status.done': 'Done',
  'ai.status.held': 'Running',
  'ai.status.failed': 'Failed',
  'ai.status.refused': 'Not enough credit',
  'ai.tabs.speech': 'Text to speech',
  'ai.tabs.transcribe': 'Speech to text',
  'ai.tabs.pictures': 'Pictures',
  'ai.unit.cost': 'Cost {{cost}}',
  'ai.unit.saved': 'a copy is in your media library',
  'ai.unit.save': 'Keep a copy in my media library',
  'ai.unit.saveHint': 'So you can send it on WhatsApp or use it again. It is stored like any file you upload.',
  'ai.unit.open': 'Open',
  'ai.unit.download': 'Download',
  'ai.speech.title': 'Text to speech',
  'ai.speech.text': 'Text to read aloud',
  'ai.speech.textHint': '{{count}} of {{max}} characters. Charged per 1 000 characters.',
  'ai.speech.voice': 'Voice',
  'ai.speech.voiceOption': '{{name}} ({{language}})',
  'ai.speech.voicesFailed': 'The voices could not be loaded just now. Please try again.',
  'ai.speech.format': 'File type',
  'ai.speech.format.mp3': 'MP3, plays everywhere',
  'ai.speech.format.ogg': 'OGG, for WhatsApp voice notes',
  'ai.speech.run': 'Read it aloud',
  'ai.speech.characters': '{{count}} characters read aloud.',
  'ai.transcribe.title': 'Speech to text',
  'ai.transcribe.recording': 'Recording',
  'ai.transcribe.recordingHint': 'An MP3, WAV, OGG, FLAC or M4A recording of up to 20 MB, such as a WhatsApp voice note.',
  'ai.transcribe.tooBig': 'That recording is too big. Choose one of up to 20 MB.',
  'ai.transcribe.language': 'Language',
  'ai.transcribe.language.auto': 'Work it out from the recording',
  'ai.transcribe.language.en-ZA': 'English (South Africa)',
  'ai.transcribe.language.af-ZA': 'Afrikaans',
  'ai.transcribe.language.zu-ZA': 'isiZulu',
  'ai.transcribe.language.en-GB': 'English (United Kingdom)',
  'ai.transcribe.language.en-US': 'English (United States)',
  'ai.transcribe.run': 'Transcribe',
  'ai.transcribe.chargeNote': 'Charged per minute of recording, by the second, with a minimum of 15 seconds. The credit is set aside first, and nothing is charged if it fails.',
  'ai.transcribe.running': 'Still transcribing. A long recording can take a few minutes. The text appears here, and under History if you leave this page.',
  'ai.transcribe.failed': 'The recording could not be transcribed. Nothing was charged.',
  'ai.transcribe.silent': 'No words were heard in this recording.',
  'ai.transcribe.length': 'The recording is {{seconds}} seconds long.',
  'ai.pictures.title': 'Make pictures',
  'ai.pictures.prompt': 'Description',
  'ai.pictures.promptHint': 'What the picture should show, e.g. a poster of a school fun day with balloons and a jumping castle, in bright colours.',
  'ai.pictures.avoid': 'Leave out',
  'ai.pictures.avoidHint': 'What the picture should not show, e.g. writing or people.',
  'ai.pictures.size': 'Shape',
  'ai.pictures.size.square': 'Square',
  'ai.pictures.size.landscape': 'Wide',
  'ai.pictures.size.portrait': 'Tall',
  'ai.pictures.count': 'How many',
  'ai.pictures.outsideSa': 'This model runs outside South Africa, so your description is sent there to make the pictures.',
  'ai.pictures.run': 'Make pictures',
  'ai.pictures.alt': 'Picture {{number}}: {{prompt}}',
  'ai.history.detail': '{{when}} · {{model}} · {{cost}} · {{status}}',
  'ai.history.saved': 'Files kept in your media library: {{count}}.'
};

export default ai;
